import React, {useEffect, useState, useMemo, useCallback} from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import { visuallyHidden } from '@mui/utils';
import CustomTablePagination from 'src/shared/TablePagination/CustomTablePagination';
import { GridContainer, Grid } from 'src/shared/grid';
import Autocomplete from '@mui/material/Autocomplete';
import { Chip, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import './FuelTypesBrowse.scss';
import { ValidCharacters, validateCharacters, errorResponse } from 'src/store/common';
import { getCategoryByType, getCodes, getDownloadHeaders } from 'src/apis/getFuelSampleCalls';
import { useAddAlert } from 'src/store/actions';
import { useStoreContext } from 'src/store/context';
import GrassIcon from '@mui/icons-material/Grass';
import CloudIcon from '@mui/icons-material/Cloud';
import ParkIcon from '@mui/icons-material/Park';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { StyledTableCellBinary, StyledTableRowBinary } from 'src/shared/Styles/Styles';
import moment from 'moment';
import { useFormikContext } from 'formik';
import { alertMessages } from 'src/constants';

export const chipIcon = (category:string, color: string)=>{
  switch (category.toLowerCase()) {
    case 'grass':
      return(<GrassIcon style={{color: `${color}`}}/>)
    case 'tree':
      return(<ParkIcon style={{color: `${color}`}}/>)
    case 'shrub':
      return(<CloudIcon style={{color: `${color}`}}/>)
    case 'forb':
      return(<LocalFloristIcon style={{color: `${color}`}}/>)
    case 'dead':
      return(<AccessTimeIcon style={{color: `${color}`}}/>)
    default:
      return (<></>)
  }
}
interface Data {
  siteId: number,
  site_name: string,
  fuelId: number,
  fuel_type: string;
  category: string;
  sub_category: string;
  method_type: string;
  sample: string;
}

function createData(
  siteId: number,
  site_name: string,
  fuelId: number,
  fuel_type: string,
  category: string,
  sub_category: string,
  method_type: string,
  sample: string,
): Data {
  return {
    siteId,
    site_name,
    fuelId,
    fuel_type,
    category,
    sub_category,
    method_type,
    sample,
  };
}

type Order = 'asc' | 'desc';

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'fuel_type',
    numeric: false,
    disablePadding: true,
    label: 'FUEL NAME',
  },
  {
    id: 'site_name',
    numeric: false,
    disablePadding: true,
    label: 'SITE NAME',
  },
  {
    id: 'category',
    numeric: false,
    disablePadding: false,
    label: 'CATEGORY',
  },
  {
    id: 'sub_category',
    numeric: true,
    disablePadding: false,
    label: 'SUB CATEGORY',
  },
  {
    id: 'method_type',
    numeric: true,
    disablePadding: false,
    label: 'METHOD',
  },
  {
    id: 'sample',
    numeric: true,
    disablePadding: false,
    label: 'LAST SAMPLE DATE',
  },
];

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
}

/*SPECIAL SORTABLE HEADER*/
function EnhancedTableHead(props: Readonly <EnhancedTableProps>) {
  const { order, orderBy, onRequestSort } = 
    props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
        <TableCell
          className="text-align-override headerParent"
          key={headCell.id}
          align={'left'}
          sortDirection={orderBy === headCell.id ? order : false}
        >
          <TableSortLabel
            active={orderBy === headCell.id}
            direction={orderBy === headCell.id ? order : 'asc'}
            onClick={createSortHandler(headCell.id)}
          >
            {headCell.label}
            {orderBy === headCell.id ? (
              <Box component="span" sx={visuallyHidden}>
                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
              </Box>
            ) : null}
          </TableSortLabel>
        </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

type FuelTypesBrowseProps={
  setDownloadFuelTypes?:any
  setDownloadCategory?:any
  setDownloadSubCategory?:any
  setDownloadMethod?:any
  setLinkGenerated?:any
  fieldSampleParams?:any
  setFieldSampleHasRows?:any
}
const FuelTypesBrowse: React.FC<FuelTypesBrowseProps>= (
  {fieldSampleParams, setDownloadFuelTypes, setDownloadCategory, setDownloadSubCategory, setDownloadMethod, setFieldSampleHasRows, setLinkGenerated}) => {
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Data>('fuel_type');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [state, dispatch] = useStoreContext();
  const addAlert = useAddAlert(dispatch);

  const allCodeObj =  useMemo(()=>({code_key: "All",code_table_id: -1, code_value:'All', type:'All'}),[]);

  const [sampleMethods, setSampleMethods] = useState<any[]>([allCodeObj]);
  const [method, setMethod] = useState<string|undefined>(sampleMethods[0].code_key);
  const [inputMethod, setInputMethod] = useState('');

  const [sampleSubCats, setSampleSubCats] = useState<any[]>([allCodeObj]);
  const [subCategory, setSubCategory] = useState<string|undefined>(sampleSubCats[0].code_key);
  const [inputSubCategory, setInputSubCategory] = useState('');
  
  const [sampleCategories, setSampleCategories] = useState<any[]>([allCodeObj]);
  const [filteredCategories, setFilteredCategories] =useState<any[]>([allCodeObj]);
  const [category, setCategory] = useState<string|undefined>(sampleCategories[0].code_key);
  const [inputCategory, setInputCategory] = useState('');

  const [filteredTypes, setFilteredTypes] =useState<any[]>([]);
  const [types, setTypes] = useState<any[]|undefined>([]);
  const [inputType, setInputType] = useState('');

  const [samples, setSamples] = useState<Data[]>([]);
  const [, setSamplesCallComplete] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState(0);
  const {values,setFieldValue} = useFormikContext<any>();

    /* this will get the fuel type and category so they are filtered on the dropdown*/
    const toGetCategory = useCallback((type?: string, cat?: string, getAll?: boolean)=>{
      (async () => {
        try {
          getAll = getAll ? getAll : false;
          const responseArray: any = await getCategoryByType(null, addAlert, type, cat, getAll);
          if (responseArray) {
            let uniqueCategories = [...new Set(responseArray.map((r: any) => r.category))];
            let uniqueTypes = [...new Set(responseArray.map((r: any) => r))];
            if(getAll){
              setFilteredTypes(uniqueTypes);
              uniqueCategories.unshift(allCodeObj.code_key);
              setFilteredCategories(uniqueCategories);
            }
            else if (cat) {
              setFilteredTypes(uniqueTypes);
            }
            else {
              uniqueCategories.unshift(allCodeObj.code_key);
              setFilteredCategories(uniqueCategories);
            }
    
          }
          else {
            setFilteredCategories([]);
            setFilteredTypes([])
          }
        }
        catch (error) {
          errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
        }
    
      })();
    },[addAlert, allCodeObj]);

    const fuelSampleResponse = useCallback((request: any) => {
      (async () => {
        try {
          const fuelSampleResponse: any = await getDownloadHeaders(fieldSampleParams.token, addAlert, request);
          const samples= fuelSampleResponse.sampleHeaders;
          const pageInfo = fuelSampleResponse.pageInfo
          setTotalCount(pageInfo.total_count);
          setSamplesCallComplete(true);
          if(samples.length > 0){
            let dataArray:Data[] = [];
            samples.forEach((sample:any)=>{
              sample.formatted_date = moment.utc(sample.latestSampleDate).utcOffset(state.userTimeZone.offset).format('MM/DD/YYYY');
              dataArray.push(createData(
                sample.site_id,
                sample.siteName,
                sample.fuelId,
                sample.fuel_type, 
                sample.category, 
                sample.sub_category,
                sample.method,
                sample.formatted_date
              ));
            }); 
            setSamples(dataArray);
            setFieldSampleHasRows(true);
          }
          else{
            setSamples([]);
            setFieldSampleHasRows(false);
          }
        } catch (error) {
          setSamplesCallComplete(true);
          setSamples([]);
          setFieldSampleHasRows(false);
          console.log('error', error)        
        }
      })();
    }, [addAlert, fieldSampleParams.token, state.userTimeZone.offset, setFieldSampleHasRows]);

  /* this use effect makes the call to get codes to update the dropdowns*/
  useEffect(() => {
    if (sampleMethods && sampleMethods.length < 2) {
      (async () => {
        try {
          const requestVars ={
            returnAll: true,
            type: null
          }
          const codeResponse: any = await getCodes(requestVars);
          toGetCategory('All', 'All', true);
          const cats = codeResponse.cats;
          cats.unshift(allCodeObj);
          const subCats = codeResponse.subCats;
          subCats.unshift(allCodeObj)
          const methods = codeResponse.methods;
          methods.unshift(allCodeObj)
          setSampleCategories(cats);
          setSampleSubCats(subCats);
          setSampleMethods(methods);
        }
        catch (error) {
          errorResponse(error, addAlert,alertMessages.fieldSampleHomeData);
        }
      })();
    }
  }, [ addAlert, allCodeObj, category, sampleMethods,
    method, subCategory, types, toGetCategory
  ]);

  /** Populate the samples table */
  useEffect(() => {
    let fuelIds:any = [];
    types?.forEach(type => {
        fuelIds.push(type.fuel_id);
    });
    const request = {
      siteId: values.sites,
      startDate: new Date(values.startDate.concat("T",values.startHour,":00:00Z")),
      endDate: new Date(values.endDate.concat("T",values.endHour,":00:00Z")),
      fuelIds: fuelIds.length > 0 ? fuelIds.join(',') : null,
      category: category,
      subCategory: subCategory,
      method: method,
      status: fieldSampleParams.includeArchivedSamples ? 'All' : 'Submitted',
      type: 'All',
      sortBy: orderBy,
      sortOrder: order,
      page: page,
      perPage: rowsPerPage,
    }

    fuelSampleResponse(request);
    setDownloadFuelTypes(types);
  },[types, category, subCategory, method, page, rowsPerPage, order, orderBy, fuelSampleResponse, setDownloadFuelTypes, fieldSampleParams.includeArchivedSamples,
    values.sites, values.endDate, values.startDate,
    values.startHour, values.endHour]);
  
  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setFieldValue('orderBy', property);
    setOrderBy(property);
    setFieldValue('order', isAsc ? 'desc' : 'asc');

  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const deleteTypeChip =(id:any)=>{
    let tempTypes = types;
    const newFiltered = tempTypes?.filter((type)=> (type.fuel_id) !==(id));
    setTypes(newFiltered);
  }

//selected might be where selection is placed
  return (
    <div style={{paddingBottom: '2.5%'}}>
        <TableContainer component={Paper} className='margin-top-2 first-table MuiPaper-root-special' style={{overflowX:'hidden'}}>
        {/*DROP DOWNS AND FILTERS*/}
        <GridContainer className="margin-top-3 dropdown-style" style={{marginLeft: '1.5%', paddingBottom:'2.5%'}}>
          <Grid row gap="md">
            <div style={{ width: '50%'}}>
              <label className="font-sans-md text-bold" htmlFor='fuelSearch'>Search Fuel Types</label>
            </div>
            <div style={{ width: '17%' }}>
              <label className="font-sans-md text-bold" htmlFor='category'>Category</label>
            </div>
            <div style={{ width: '16%' }}>
              <label className="font-sans-md text-bold" htmlFor='subCategory'>Sub-Category</label>
            </div>
            <div style={{ width: '15%' }}>
              <label className="font-sans-md text-bold" htmlFor='method'>Method</label>
            </div>
          </Grid>
          <Grid row gap="md" className="margin-top-1">

            <div style={{ width: '50%' }}>
              <div className='searchFuels'>
                <i className="fas fa-search" />
                <Autocomplete
                  multiple
                  limitTags={-1}
                  value={types}
                  onChange={(event: any, newValue: any) => {
                    setTypes(newValue);
                    toGetCategory(newValue);
                    setLinkGenerated(false);
                  }}
                  inputValue={inputType}
                  onInputChange={(event, newInputValue) => {
                    setInputType(newInputValue);
                    setLinkGenerated(false);
                  }}
                  aria-labelledby='fuelSearch'
                  id="fuelSearch"
                  options={filteredTypes.length > 1 ? (filteredTypes) : ([])}
                  getOptionLabel={(option) => option.fuel_type}
                  renderInput={(params) => (
                      <TextField 
                        {...params} 
                        placeholder="Search Fuel Type"
                        InputProps={{
                          ...params.InputProps,
                          type: 'search',
                          style: {height:'auto'},
                        }}
                        style={{height: 'auto'}}
                        InputLabelProps={{
                          shrink:true,
                          style:{visibility: 'hidden'}
                        }}
                      />
                  )}
                  renderTags={(value,getTagProps)=>
                    value.map((option, index)=>(
                      
                      <Chip
                        icon={chipIcon(option.category, 'white')} 
                        label={option.fuel_type}
                        {...getTagProps({index})}
                        onDelete={()=>deleteTypeChip(option.fuel_id)}
                        tabIndex={0}
                        onKeyDown={(event)=>{event.key==="Backspace" && deleteTypeChip(option.fuel_id)}}
                        deleteIcon={<CloseIcon tabIndex={0} onKeyDown={(event)=>{event.key==="Enter" && deleteTypeChip(option.fuel_id)}}/>}
                        className={index===0?"firstFuelChip":"fuelChip"}
                      />
                    ))
                  }
        
                />
              </div>
            </div>
            <div style={{ width: '17%' }}>
              <Autocomplete
                className="height-55"
                value={category}
                onChange={(event: any, newValue: any) => {
                  setCategory(newValue);
                  setDownloadCategory(newValue);
                  setLinkGenerated(false);
                }}
                inputValue={inputCategory}
                onInputChange={(event, newInputValue) => {
                  setInputCategory(newInputValue);
                  setLinkGenerated(false);
                }}
                disableClearable
                id="category"
                options={filteredCategories.length > 1 ? (filteredCategories) : (sampleCategories.map((option) => option.code_key))}
                aria-labelledby='category'
                renderInput={(params) => (
                  <TextField
                    onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                    data-testid={'category'}
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      type: 'search',
                    }}
                    InputLabelProps={{
                      shrink:true,
                      style:{visibility: 'hidden'}
                    }}
                  />
                )}
              />
            </div>
            <div style={{ width: '16%' }}>
              <Autocomplete
                className="height-55"
                value={subCategory}
                onChange={(event: any, newValue: any) => {
                  setSubCategory(newValue);
                  setDownloadSubCategory(newValue);
                  setLinkGenerated(false);
                }}
                inputValue={inputSubCategory}
                onInputChange={(event, newInputValue) => {
                  setInputSubCategory(newInputValue);
                  setLinkGenerated(false);
                }}                
                disableClearable
                aria-labelledby='subCategory'
                id="subCategory"
                options={sampleSubCats.map((option) => option?.code_key)}
                renderInput={(params) => (
                  <TextField
                    onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                    data-testid={'sub_category'}
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      type: 'search',
                    }}
                    InputLabelProps={{
                      shrink:true,
                      style:{visibility: 'hidden'}
                    }}
                  />
                )}
              />
            </div>
            <div style={{ width: '15%' }}>
            <Autocomplete
                className="height-55"
                value={method}
                onChange={(event: any, newValue: any) => {
                  setMethod(newValue);
                  setDownloadMethod(newValue);
                  setLinkGenerated(false);
                }}
                inputValue={inputMethod}
                onInputChange={(event, newInputValue) => {
                  setInputMethod(newInputValue);
                  setLinkGenerated(false);
                }}
                disableClearable
                id="method"
                aria-labelledby='method'
                options={sampleMethods.map((option) => option?.code_key)}
                renderInput={(params) => (
                  <TextField
                    onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                    data-testid={'method'}
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      type: 'search',
                    }}
                    InputLabelProps={{
                      shrink:true,
                      style:{visibility: 'hidden'}
                    }}
                  />
                )}
              />
            </div>
          </Grid>
        </GridContainer>

        {/* TABLE FOR FUELS*/}
          <Table
            sx={{ minWidth: 750 }}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {                
                samples.map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <StyledTableRowBinary
                      tabIndex={-1}
                      key={`${row.siteId}|${row.fuelId}|${row.category}|${row.sub_category}|${row.method_type}`}
                    >
                      <StyledTableCellBinary className='text-align-override'
                        component="th"
                        id={labelId}
                        scope="row"
                        tabIndex={0}
                        padding="none"
                      >
                        {row.fuel_type}
                      </StyledTableCellBinary>
                      <StyledTableCellBinary className='text-align-override'
                        tabIndex={0}
                      >{row.site_name}</StyledTableCellBinary>
                      <StyledTableCellBinary className='text-align-override' 
                        component="td"
                        tabIndex={0}
                      >
                        {row.category}
                      </StyledTableCellBinary>
                      <StyledTableCellBinary className='text-align-override'
                        tabIndex={0}
                      >{row.sub_category}</StyledTableCellBinary>
                      <StyledTableCellBinary className='text-align-override'
                        tabIndex={0}
                      >{row.method_type}</StyledTableCellBinary>
                      <StyledTableCellBinary className='text-align-override'
                        tabIndex={0}
                      >{row.sample}</StyledTableCellBinary>
                    </StyledTableRowBinary>
                  );
                })
              }
            </TableBody>
          </Table>
          {(!samples || ( samples && samples.length<1))
            && <GridContainer className="removePadding">
              <Grid row gap="md" className='page-heading'>
                <Grid
                  tabIndex={0}
                  desktop={{ col: 12 }}
                  style={{paddingTop: '2.5%', marginLeft: '1%', marginBottom: '2.5%'}}
                  className="display-flex flex-row flex-align-center flex-justify"
                >    
                  <span className="font-sans-sm text-bold">0 Samples have been collected at this site for the selected filters.</span>
                </Grid>
              </Grid>
            </GridContainer>
          }
        </TableContainer>
        <CustomTablePagination
          rowsPerPageOptions={[10, 20, 30]}
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
    </div>
  );
}
export default React.memo(FuelTypesBrowse);